import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const RETRY_FLAG_KEY = 'nebula:dynamic-import-retried'
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk [\w-]+ failed/i,
  /ChunkLoadError/i,
]

export function isDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error)
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

export function isChunkLoadErrorEvent(event: {
  exception?: { values?: { type?: string; value?: string }[] }
}): boolean {
  const values = event.exception?.values
  if (!values) return false
  return values.some((ex) => {
    const msg = ex.value ?? ''
    const full = ex.type ? `${ex.type}: ${msg}` : msg
    return DYNAMIC_IMPORT_ERROR_PATTERNS.some((p) => p.test(full))
  })
}

function reloadWithCacheBust(): void {
  const currentUrl = new URL(window.location.href)
  currentUrl.searchParams.set('__chunk_retry', Date.now().toString())
  window.location.replace(currentUrl.toString())
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await importer()
      sessionStorage.removeItem(RETRY_FLAG_KEY)
      return module
    } catch (error) {
      const hasRetried = sessionStorage.getItem(RETRY_FLAG_KEY) === '1'

      if (!hasRetried && isDynamicImportError(error)) {
        sessionStorage.setItem(RETRY_FLAG_KEY, '1')
        reloadWithCacheBust()

        // Keep this render suspended while the browser navigates.
        return new Promise<{ default: T }>(() => {})
      }

      throw error
    }
  })
}
